import React from 'react';
import { Device } from '../../lib/types';
import { findFosterHomeForDevice, type FosterHome } from '../../lib/fosterAdapters';
import { isFosterContactComplete, formatDate, formatDaysAgo } from '../../lib/utils';
import StatusBadge from '../../components/StatusBadge/StatusBadge';
import Button from '../../components/Button/Button';
import ActionMenu, { type ActionMenuEntry } from '../../components/ActionMenu/ActionMenu';
import DeviceCell from './DeviceCell';
import DeviceStatusCell from './DeviceStatusCell';
import PetNameCell from './PetNameCell';
import styles from './Devices.module.scss';

interface DeviceRowProps {
  device: Device;
  onAddPet: (device: Device) => void;
  onTransferFoster: (device: Device) => void;
  onRecall: (device: Device) => void;
  onTransferAdopter: (device: Device) => void;
  /** One-click adopt-out using the foster household's info (Fosters table only). */
  onAdoptFromFoster?: (device: Device) => void;
  /** Opens the saved foster home behind this placement (Fosters table only). */
  onViewFoster?: (home: FosterHome) => void;
  /** The rescue's saved homes, used to resolve the foster behind a placement for onViewFoster. */
  fosterHomes?: FosterHome[];
  onSendSms: (device: Device) => void;
  /** Pet IDs whose adoption transfer is complete — hides rescue mutation actions. */
  adoptedPetIds?: Set<string>;
  /** True while the adopted-pet lookup is in flight — gate actions optimistically. */
  adoptedLoading?: boolean;
  /** Show the Holder cell (kept in sync with the table's Holder column). */
  showHolder?: boolean;
  /** Show the Placed On cell (kept in sync with the table's Placed On column). */
  showPlacedOn?: boolean;
}

export default function DeviceRow({
  device,
  onAddPet,
  onTransferFoster,
  onRecall,
  onTransferAdopter,
  onAdoptFromFoster,
  onViewFoster,
  fosterHomes,
  onSendSms,
  adoptedPetIds,
  adoptedLoading,
  showHolder = true,
  showPlacedOn = false,
}: DeviceRowProps) {
  const isOnline = device.status === 'online';
  const petAdopted = adoptedLoading || !!adoptedPetIds?.has(device._petGraphqlId);

  // Setup pills
  const szVariant = device.safeZone ? 'success' : 'error';
  const wifiVariant = device.wifiConfigured ? 'success' : 'error';

  // Actions depend on holder type and dog assignment. Anything beyond a single action goes
  // into a menu rather than a row of buttons — the Action column was the widest on the table
  // and the least informative, and these tables need the width for their data columns.
  const menuLabel = device.petName ? `Actions for ${device.petName}` : `Actions for device ${device.id}`;
  let actions: React.ReactNode;
  if (device.holderType === 'rescue' && !device.petName) {
    // A lone action stays a plain button; a menu wrapping one item is a click for nothing.
    actions = (
      <Button variant="green" size="sm" onClick={() => onAddPet(device)}>
        Add Pet
      </Button>
    );
  } else if (device.holderType === 'foster') {
    // Adopt-out to the foster household in one click. Disabled when the foster's
    // contact info is incomplete, since the transfer sends the adopter an email.
    const canAdoptFromFoster = isFosterContactComplete(device);
    // Only placements made with `saveToRoster` have a home to show, so the entry is omitted
    // rather than disabled when the lookup comes up empty.
    const fosterHome = onViewFoster && fosterHomes ? findFosterHomeForDevice(device, fosterHomes) : null;
    const items: ActionMenuEntry[] = [
      { label: 'Send SMS', onSelect: () => onSendSms(device) },
      !!fosterHome && { label: 'View Foster', onSelect: () => onViewFoster?.(fosterHome) },
      {
        label: 'Adopt (Foster Fail)',
        onSelect: () => onAdoptFromFoster?.(device),
        disabled: !canAdoptFromFoster,
        title: canAdoptFromFoster ? undefined : "Add this foster's email and phone number first",
      },
      { label: 'Recall', onSelect: () => onRecall(device), danger: true, dividerBefore: true },
    ];
    actions = <ActionMenu items={items} ariaLabel={menuLabel} />;
  } else if (petAdopted) {
    actions = <span className={styles.textMuted}>&mdash;</span>;
  } else {
    const items: ActionMenuEntry[] = [
      { label: 'Adopt', onSelect: () => onTransferAdopter(device) },
      { label: 'Foster', onSelect: () => onTransferFoster(device) },
      { label: 'Unlink Device', onSelect: () => onRecall(device), danger: true, dividerBefore: true },
    ];
    actions = <ActionMenu items={items} ariaLabel={menuLabel} />;
  }

  return (
    <tr>
      {showHolder && (
        <td>
          <span className={styles.holderName}>{device.holderName}</span>
        </td>
      )}
      <td>
        {device.petName ? (
          <PetNameCell name={device.petName} photoThumbUrl={device.petPhotoThumbUrl} />
        ) : (
          <span className={styles.textMuted}>Unassigned</span>
        )}
      </td>
      <td>
        <DeviceCell moduleId={device.id} type={device.type} showBattery battery={device.battery} />
      </td>
      {showPlacedOn && (
        <td className={styles.nowrap}>
          {/* `transferDate` on an IN_FOSTER row is the placement date — see the Device type. */}
          <div className={styles.cellMain}>{formatDate(device.transferDate)}</div>
          {device.transferDate && <div className={styles.cellSub}>{formatDaysAgo(device.transferDate)}</div>}
        </td>
      )}
      <td>
        <div className={styles.setupPills}>
          <StatusBadge variant={szVariant}>Safe Zone</StatusBadge>
          <StatusBadge variant={wifiVariant}>WiFi</StatusBadge>
        </div>
      </td>
      <td>
        <DeviceStatusCell isOnline={isOnline} lastSeen={device.lastSeen} />
      </td>
      <td>{actions}</td>
    </tr>
  );
}
